import EventEmitter from "eventemitter3";
import debug from "debug";
import { isLoadablePlugin, isLoadedPlugin, isPluginDescription, } from "./guards.js";
const log = debug("patchwork:plugins");
function isAsyncFunction(fn) {
    return (typeof fn == "function" &&
        Symbol.toStringTag in fn &&
        fn[Symbol.toStringTag] == "AsyncFunction");
}
/**
 * Registry for managing plugins of a specific type
 * D = Description type that extends PluginDescription
 * I = Implementation type that will be loaded and combined with the description
 */
export class PluginRegistry {
    #plugins = new Map();
    #loadPromises = new Map();
    #events = new EventEmitter();
    #loading = new Set();
    /**
     * Register an plugin with this registry
     */
    register(plugin, importUrl) {
        // If an import URL was provided, attach it to the plugin
        if (importUrl && !plugin.importUrl) {
            plugin.importUrl = importUrl;
        }
        const existing = this.#plugins.get(plugin.id);
        if (existing) {
            if (existing.importUrl == importUrl) {
                log(`updating ${plugin.id} provided by "${existing.importUrl}"`);
            }
            else {
                console.warn(`overriding "${plugin.id}" provided by "${existing.importUrl}" with new plugin provided by "${importUrl}"`);
            }
        }
        this.#plugins.set(plugin.id, plugin);
        this.#loadPromises.delete(plugin.id);
        this.#loading.delete(plugin.id);
        this.#events.emit("registered", plugin);
        this.#events.emit("changed");
    }
    /**
     * Get an plugin by ID, returning either its description or loaded state
     */
    get(id) {
        return this.#plugins.get(id);
    }
    /** Get all plugins, both descriptions and loaded */
    all() {
        const entries = Array.from(this.#plugins.values());
        return entries;
    }
    /** Return a filtered list of plugins */
    filter(filter) {
        return this.all().filter(filter);
    }
    /**
     * Load a plugin by id, waiting for registration if the plugin has not been
     * registered yet. Useful when a consumer depends on a plugin that will be
     * registered by a later-loaded module bundle (e.g. the `account` datatype
     * which ships with the `patchwork-frame` bundle).
     */
    async loadWhenReady(id) {
        const immediate = await this.load(id);
        if (immediate)
            return immediate;
        return new Promise((resolve, reject) => {
            const off = this.on("registered", async (plugin) => {
                if (plugin.id !== id)
                    return;
                off();
                try {
                    const loaded = await this.load(id);
                    if (!loaded) {
                        reject(new Error(`Plugin "${id}" registered but failed to load`));
                        return;
                    }
                    resolve(loaded);
                }
                catch (error) {
                    reject(error);
                }
            });
        });
    }
    /**
     * Load an plugin by ID, loading it on demand if necessary (asynchronous)
     * If shouldWait is true, will wait for the plugin to be registered if it isn't already
     */
    async load(id) {
        log(`load called for: ${id}`, {});
        const description = this.#plugins.get(id);
        if (!description) {
            log(`Plugin not registered: ${id}`);
            return undefined;
        }
        if (isLoadedPlugin(description)) {
            log(`Returning already loaded plugin: ${id}`);
            return description;
        }
        const existingPromise = this.#loadPromises.get(id);
        if (existingPromise) {
            log(`Returning in-flight load for: ${id}`);
            return existingPromise;
        }
        if (!isLoadablePlugin(description)) {
            throw new Error(`Plugin ${id} is not loadable`);
        }
        log(`Loading plugin implementation: ${id}`);
        this.#loading.add(id);
        const loadPromise = ("load" in description
            ? description.load()
            : import(description.import))
            .then((implementation) => {
            const clearIfOurs = () => {
                if (this.#loadPromises.get(id) === loadPromise) {
                    this.#loadPromises.delete(id);
                    this.#loading.delete(id);
                }
            };
            // If the plugin was removed or re-registered while loading, discard
            if (this.#plugins.get(id) !== description) {
                log(`Plugin ${id} was replaced or removed during load, discarding`);
                clearIfOurs();
                return undefined;
            }
            log(`Successfully loaded implementation for: ${id}`, implementation);
            // TODO: remove this
            const desc = description;
            if (desc.type == "patchwork:datatype") {
                const impl = implementation;
                if (isAsyncFunction(impl.getTitle)) {
                    console.warn(desc.id, desc.importUrl, "getTitle should not be an async function");
                }
                if (isAsyncFunction(impl.setTitle)) {
                    console.warn(desc.id, desc.importUrl, "setTitle should not be an async function");
                }
            }
            const { load: _load, import: _import, ...descriptionWithoutLoad } = desc;
            if (!isPluginDescription(descriptionWithoutLoad)) {
                throw new Error("Invalid plugin description");
            }
            const plugin = {
                ...descriptionWithoutLoad,
                module: implementation,
            };
            this.#plugins.set(id, plugin);
            clearIfOurs();
            this.#events.emit("loaded", plugin);
            this.#events.emit("changed");
            return plugin;
        })
            .catch((error) => {
            console.error(`Failed to load plugin implementation: ${id}`, error);
            if (this.#loadPromises.get(id) === loadPromise) {
                this.#loadPromises.delete(id);
                this.#loading.delete(id);
            }
            throw error;
        });
        this.#loadPromises.set(id, loadPromise);
        return loadPromise;
    }
    /**
     * Load all provided plugins
     * @returns A promise resolving to an array of loaded plugins
     */
    async loadAll(plugins) {
        // Get all plugins or filter them if a filter function is provided
        // Create an array of promises for loading each plugin
        const loadPromises = plugins.map((plugin) => this.load(plugin.id));
        const results = await Promise.allSettled(loadPromises);
        return results.flatMap((result) => result.status === "fulfilled" && result.value !== undefined
            ? [result.value]
            : []);
    }
    /**
     * Remove a plugin by ID. Emits "removed" and "changed" if the plugin existed.
     * Returns true if the plugin was found and removed, false if it wasn't registered.
     */
    remove(id) {
        if (!this.#plugins.has(id)) {
            return false;
        }
        this.#plugins.delete(id);
        this.#loadPromises.delete(id);
        this.#loading.delete(id);
        this.#events.emit("removed", id);
        this.#events.emit("changed");
        return true;
    }
    /**
     * Check if a plugin is currently being loaded
     */
    isLoading(id) {
        return this.#loading.has(id);
    }
    /**
     * Check if an plugin ID is registered
     */
    has(id) {
        return this.#plugins.has(id);
    }
    on(event, callback) {
        if (!callback || typeof callback !== "function") {
            throw new Error(`Invalid callback provided for event: ${event}`);
        }
        this.#events.on(event, callback);
        return () => {
            this.#events.off(event, callback);
        };
    }
}
